import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import StudentPicker from './components/StudentPicker';
import CheckInFlow from './components/CheckInFlow';
import SuccessScreen from './components/SuccessScreen';

// ---------------------------------------------------------------------------
// Student Check-In  (route: /check-in  and  /check-in/:studentId)
//
// A student-facing self-monitoring screen. Opened by the teacher/para on
// a class iPad or the student's Chromebook. The student rates themselves
// on each of their active IEP goals ("I did it!" / "Kind of" / "Not
// today"), optionally taps a Zones-of-Regulation feeling, and each tap
// becomes a real data point on goal_progress_entries with
// source='student_self'. Teachers see the exact same time series they
// already track on the goal detail view — self check-ins are just
// visually flagged so it's clear who entered them.
//
// UX principles this page is optimized for:
//   * Reading load is minimal — a young student or a student with a
//     communication disability might use this without adult narration.
//   * Every tap moves forward or gives immediate feedback. No form
//     fields, no dropdowns, no keyboards.
//   * The teacher-facing student-picker screen looks different from
//     the student-facing rating screen so an adult sets it up and hands
//     the device over.
//   * Only the student's ACTIVE goals appear (status in
//     not_started / in_progress). Mastered and discontinued goals are
//     hidden so we never ask a kid to self-rate on something that's
//     "done."
// ---------------------------------------------------------------------------

const CheckInPage = () => {
  const navigate = useNavigate();
  const { studentId: routeStudentId } = useParams();

  const { students, loading: studentsLoading } = useStudents();
  const { goals, logProgress, loading: goalsLoading } = useIEPGoals();

  const [pickedStudentId, setPickedStudentId] = useState(routeStudentId || '');
  const [phase, setPhase] = useState(routeStudentId ? 'flow' : 'picker'); // 'picker' | 'flow' | 'success'
  const [completedGoalIds, setCompletedGoalIds] = useState([]);
  const [savedFeeling, setSavedFeeling] = useState(null);

  const student = useMemo(
    () => (students || []).find((s) => s.id === pickedStudentId) || null,
    [students, pickedStudentId],
  );

  // Only the goals we want to ask a student to self-rate: not_started
  // or in_progress. Deliberately excludes mastered/discontinued so we
  // never ask a kid "how did you do on the thing you already mastered?"
  const activeGoalsForStudent = useMemo(() => {
    if (!student) return [];
    return (goals || [])
      .filter(
        (g) =>
          g.studentId === student.id &&
          (g.status === 'not_started' || g.status === 'in_progress'),
      )
      .sort((a, b) => {
        const aStr = (a.goalArea || '') + (a.specific || '');
        const bStr = (b.goalArea || '') + (b.specific || '');
        return aStr.localeCompare(bStr);
      });
  }, [goals, student]);

  const handlePickStudent = (id) => {
    setPickedStudentId(id);
    setPhase('flow');
    setCompletedGoalIds([]);
    setSavedFeeling(null);
    // Keep the URL in sync so a refresh doesn't kick the student back
    // to the picker mid-flow — nothing worse for a 6-year-old than
    // losing their place because the iPad screen dimmed.
    navigate(`/check-in/${id}`, { replace: true });
  };

  const handleFlowComplete = ({ ratedGoalIds, feeling }) => {
    setCompletedGoalIds(ratedGoalIds);
    setSavedFeeling(feeling);
    setPhase('success');
  };

  const handleStartOver = () => {
    setPickedStudentId('');
    setPhase('picker');
    setCompletedGoalIds([]);
    setSavedFeeling(null);
    navigate('/check-in', { replace: true });
  };

  const handleExit = () => {
    navigate('/dashboard');
  };

  const loading = studentsLoading || goalsLoading;

  // Kid-friendly full-screen shell — no sidebar, no top header. The
  // idea is that when the adult hands the device to the student,
  // nothing on the screen invites them to wander off to Reports or
  // Team pages.
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-white">
      {/* Adult-facing exit button in the top corner. Small and out of
          the student's normal tap zone. */}
      <div className="absolute top-3 right-3 z-10">
        <button
          type="button"
          onClick={handleExit}
          className="inline-flex items-center gap-1 rounded-full bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Back to teacher dashboard"
        >
          <Icon name="LogOut" size={12} />
          Teacher exit
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {loading ? (
          <div className="text-center py-16 text-gray-600">
            <Icon
              name="Loader2"
              size={40}
              className="animate-spin mx-auto mb-3 text-primary"
            />
            <p>Loading…</p>
          </div>
        ) : phase === 'picker' || !student ? (
          <StudentPicker
            students={students || []}
            onPick={handlePickStudent}
          />
        ) : phase === 'flow' ? (
          <CheckInFlow
            student={student}
            goals={activeGoalsForStudent}
            logProgress={logProgress}
            onComplete={handleFlowComplete}
            onSwitchStudent={handleStartOver}
          />
        ) : (
          <SuccessScreen
            student={student}
            ratedGoalCount={completedGoalIds.length}
            feeling={savedFeeling}
            onCheckInAgain={handleStartOver}
            onExit={handleExit}
          />
        )}
      </div>
    </div>
  );
};

export default CheckInPage;
